/* eslint-disable max-params */
import type { ChatAppRequest, ServerMessage } from "../models"

import {
  registerSessionWebSocket,
  unregisterSessionWebSocket,
} from "@/features/chat/utils/websocket-session-registry"
import { logger } from "@/lib/logger"

import { WebSocketClient } from "../web-socket-client"
import { BACKEND_URI } from "./_shared"

/**
 * STREAMING: Explorer chat via WebSocket (real-time streaming)
 * Note: This is a WebSocket operation, not a standard query/mutation
 * @param request - Chat request data
 * @param files - Optional files to include
 * @param idToken - Authentication token
 * @param signal - Abort signal for cancellation
 * @param onMessage - Callback for incoming messages
 * @param routeId - Route ID used to target the correct explorer variant
 * @param sessionId - Optional session ID for WebSocket registry (needed for tool approvals)
 * @returns Response promise
 * @category Streaming
 */
export async function explorerChatWebSocketApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal,
  onMessage: (data: ServerMessage) => void,
  routeId: string,
  sessionId?: string | null,
  onOpen?: () => void
): Promise<Response> {
  if (!idToken) {
    return Promise.reject(new Error("Missing authentication token."))
  }
  if (!routeId.trim()) {
    return Promise.reject(new Error("Missing explorer route ID."))
  }

  const routeSegment = encodeURIComponent(routeId)
  const wsUrl = `${BACKEND_URI}/ws/explorer-chat/${routeSegment}`

  // For streaming requests, we need to convert the WebSocket to a stream
  if (request.stream) {
    // Create WebSocket client
    const wsClient = new WebSocketClient({
      url: wsUrl,
      idToken,
      requestData: request,
      files,
      signal,
      onHeartbeat: (timestamp: number) => {
        logger.debug(`Heartbeat received at ${new Date(timestamp * 1000).toISOString()}`)
      },
      onOpen,
      onMessage,
      onConnectionClosed: () => {
        if (sessionId) {
          unregisterSessionWebSocket(sessionId, wsClient)
        }
      },
    })
    if (sessionId) {
      registerSessionWebSocket(sessionId, wsClient)
    }

    // Simply return the streaming response
    return wsClient.createStreamingResponse()
  } else {
    // For non-streaming requests, use the WebSocket client directly
    const wsClient = new WebSocketClient({
      url: wsUrl,
      idToken,
      requestData: request,
      files,
      signal,
      onMessage,
      onConnectionClosed: () => {
        if (sessionId) {
          unregisterSessionWebSocket(sessionId, wsClient)
        }
      },
    })
    if (sessionId) {
      registerSessionWebSocket(sessionId, wsClient)
    }

    try {
      const response = await wsClient.getPromise()
      return new Response(JSON.stringify(response), {
        headers: { "Content-Type": "application/json" },
      })
    } catch (error) {
      return Promise.reject(error)
    }
  }
}

/**
 * STREAMING: Scholar chat via WebSocket (real-time streaming)
 * Note: This is a WebSocket operation, not a standard query/mutation
 * @param request - Chat request data
 * @param files - Optional files to include
 * @param idToken - Authentication token
 * @param signal - Abort signal for cancellation
 * @param onMessage - Optional callback for incoming messages
 * @returns Response promise
 * @category Streaming
 */
export async function scholarChatWebSocketApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal,
  onMessage?: (data: ServerMessage) => void,
  onOpen?: () => void
): Promise<Response> {
  if (!idToken) {
    return Promise.reject(new Error("Missing authentication token."))
  }

  // For streaming requests, we need to convert the WebSocket to a stream
  if (request.stream) {
    // Create WebSocket client
    const wsClient = new WebSocketClient({
      url: `${BACKEND_URI}/ws/scholar`,
      idToken,
      requestData: request,
      files,
      signal,
      onHeartbeat: (timestamp: number) => {
        logger.debug(`Heartbeat received at ${new Date(timestamp * 1000).toISOString()}`)
      },
      onOpen,
      onMessage,
    })

    // Simply return the streaming response
    return wsClient.createStreamingResponse()
  } else {
    // For non-streaming requests, use the WebSocket client directly
    const wsClient = new WebSocketClient({
      url: `${BACKEND_URI}/ws/scholar`,
      idToken,
      requestData: request,
      files,
      signal,
      onMessage,
    })

    try {
      const response = await wsClient.getPromise()
      return new Response(JSON.stringify(response), {
        headers: { "Content-Type": "application/json" },
      })
    } catch (error) {
      return Promise.reject(error)
    }
  }
}

/**
 * STREAMING: Deep Explorer chat via WebSocket (LangGraph + langchain-deepagents runtime).
 *
 * This is a parallel runtime to plain Explorer (Responses API) and Explorer Agent
 * (custom LangGraph research agent). Deep Explorer uses `deepagents.create_deep_agent`
 * to give the agent `write_todos`, a virtual scratchpad filesystem, and sub-agents.
 *
 * Walking-skeleton phase: sends user messages, receives streamed activity updates
 * through the same WebSocket envelope as the other explorer endpoints. Phase 2 will
 * add a `deep_state_snapshot` frame type for the side-panel todos/files UI.
 *
 * @category Streaming
 */
export async function explorerDeepChatWebSocketApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal,
  onMessage?: (data: ServerMessage) => void,
  onClientReady?: (send: (message: Record<string, unknown>) => void) => void,
  onConnectionClosed?: () => void,
  onOpen?: () => void
): Promise<Response> {
  if (!idToken) {
    return Promise.reject(new Error("Missing authentication token."))
  }

  const correlatedRequest: ChatAppRequest = {
    ...request,
    client_capabilities: {
      ...request.client_capabilities,
      deep_ask_user_response_ids: true,
    },
  }

  if (request.stream) {
    const wsClient = new WebSocketClient({
      url: `${BACKEND_URI}/ws/explorer-deep`,
      idToken,
      requestData: correlatedRequest,
      files,
      signal,
      onHeartbeat: (timestamp: number) => {
        logger.debug(`Deep Explorer heartbeat at ${new Date(timestamp * 1000).toISOString()}`)
      },
      onOpen,
      onMessage,
      onConnectionClosed,
    })

    // Expose the send function so callers can push inbound control frames
    // (e.g., deep_plan_decision) to the backend over the same WebSocket.
    if (onClientReady) {
      onClientReady((message) => wsClient.send(message))
    }

    return wsClient.createStreamingResponse()
  } else {
    const wsClient = new WebSocketClient({
      url: `${BACKEND_URI}/ws/explorer-deep`,
      idToken,
      requestData: correlatedRequest,
      files,
      signal,
      onMessage,
      onConnectionClosed,
    })

    if (onClientReady) {
      onClientReady((message) => wsClient.send(message))
    }

    try {
      const response = await wsClient.getPromise()
      return new Response(JSON.stringify(response), {
        headers: { "Content-Type": "application/json" },
      })
    } catch (error) {
      return Promise.reject(error)
    }
  }
}
